// Direct imports
import { Scene } from "@babylonjs/core/scene";
import "@babylonjs/core/Engines/Extensions/engine.videoTexture"; // Side effect: registers ThinEngine.updateVideoTexture (video frame → GPU upload). Without it the method is a silent no-op stub and the video mesh never becomes render-ready.
import { VideoTexture } from "@babylonjs/core/Materials/Textures/videoTexture";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import "@babylonjs/core/Meshes/Builders/planeBuilder"; // Side effect for CreatePlane
import { Observer } from "@babylonjs/core/Misc/observable";
import { Nullable } from "@babylonjs/core/types";
import { PointerEventTypes } from "@babylonjs/core/Events/pointerEvents";
import type { PointerInfoPre } from "@babylonjs/core/Events/pointerEvents";
import { Observable } from "@babylonjs/core/Misc/observable";
import { AdvancedDynamicTexture } from "@babylonjs/gui/2D/advancedDynamicTexture";
import { Ellipse } from "@babylonjs/gui/2D/controls/ellipse";
import { TextBlock } from "@babylonjs/gui/2D/controls/textBlock";
import { Rectangle } from "@babylonjs/gui/2D/controls/rectangle";
import { Control } from "@babylonjs/gui/2D/controls/control";
import { SceneObject } from '../core/SceneObject';
import { Graphic } from './graphic';
import { BoxCollider, COLLIDER_MESH_SUFFIX } from './boxcollider';
import { VideoData } from '../scenedata/behaviors/videodata';
import { Orientation } from '../scenedata/common';
import { Logger } from '../utils/Logger';
import { ValidationError } from '../scenedata/behaviordata';
import { mediaElementClockTime, type Playable, type PlayableClock, type PlayableSource, type PlayableVolume } from './playable';

/** Suffix for meshes that host interactive GUI controls and must remain pickable in play mode */
export const CONTROLS_MESH_SUFFIX = "__controls";

/** Height of the controls bar in world units (fraction of the video quad's unit height) */
const CONTROLS_BAR_HEIGHT = 0.15;

/** Track width as a fraction of the bar (centered). Drives both the GUI layout and UV→scrub mapping. */
const CONTROLS_TRACK_WIDTH = 0.85;

// 2024
// Author: Aaron Post

/** Format seconds as m:ss */
function formatTime(seconds: number): string {
    if (!isFinite(seconds) || seconds < 0) return "0:00";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * Shared cache for video textures by URL to avoid duplicate network requests.
 *
 * Note: Currently all instances sharing a texture share the same playback state
 * (play/pause, current time). If independent playback per instance is needed,
 * we'd need to cache the fetched video blob instead and create separate
 * VideoTexture instances from it, or rely on browser HTTP caching.
 */
const videoTextureCache = new Map<string, {
    texture: VideoTexture;
    refCount: number;
}>();

export class Video extends Graphic<VideoData> implements Playable, PlayableSource, PlayableVolume, PlayableClock {
    static readonly behaviorType = "Video" as const;
    private videoTexture: VideoTexture | null;
    private quad: Mesh | null;
    private videoMaterial: StandardMaterial | null = null;
    public aspectRatio: number;
    public orientation: Orientation = Orientation.Vertical;

    /**
     * Fires when the underlying <video> element is ready (metadata loaded).
     * Subscribers receive the HTMLVideoElement and may attach Web Audio nodes
     * (e.g. AudioSource) or inspect dimensions/duration.
     * If metadata is already loaded when a subscriber adds, it fires immediately.
     */
    public readonly onMediaElementReadyObservable = new Observable<HTMLVideoElement>();

    /** The resolved video URL (for cache lookup on dispose) */
    private _resolvedVideoUrl: string | null = null;

    /** Deferred loading state */
    private _videoLoaded = false;
    private _renderObserver: Nullable<Observer<Scene>> = null;

    /** Loading indicator */
    private _loadingPlane: Mesh | null = null;
    private _loadingTexture: AdvancedDynamicTexture | null = null;
    private _spinnerObserver: Nullable<Observer<Scene>> = null;

    /** Tap-to-play overlay */
    private _playButtonPlane: Mesh | null = null;
    private _playButtonTexture: AdvancedDynamicTexture | null = null;

    /** Invisible picker mesh for tap-to-play (only created when no BoxCollider exists) */
    private _pickerMesh: Mesh | null = null;

    /** Timeline controls overlay */
    private _controlsPlane: Mesh | null = null;
    /** Fitted video width the controls geometry was built for */
    private _controlsFittedWidth = 0;
    private _controlsTexture: AdvancedDynamicTexture | null = null;
    private _controlsUpdateObserver: Nullable<Observer<Scene>> = null;
    private _controlsPointerObserver: Nullable<Observer<PointerInfoPre>> = null;
    private _progressFill: Rectangle | null = null;
    private _timeText: TextBlock | null = null;
    private _isScrubbing = false;
    /** Set when the controls bar consumed a pointer-down, to suppress tap-to-play toggle.
     *  A hand-rolled claim bit between two dispatch pipelines (the raw
     *  pre-observable below vs TraceScene's onPointerEvent) — goes away when
     *  the leaf dispatch gains pick context + a claim return (#622 tier 2). */
    private _controlsConsumedPointer = false;

    /** When true, commit the detected aspect ratio once metadata arrives */
    private _authorAspectOnDetect = false;

    protected getFitQuad(): Mesh | null {
        return this.quad;
    }

    protected getFitMode(): 'contain' | 'cover' {
        return this._resolved.fit ?? 'contain';
    }

    protected getCornerRadius(): number {
        return this._resolved.cornerRadius ?? 0;
    }

    protected getPill(): boolean {
        return this._resolved.pill === true;
    }

    protected getBack(): 'none' | 'picture' | 'color' {
        return this._resolved.back ?? 'none';
    }

    protected getBackColor(): string {
        return this._resolved.backColor ?? '#FFFFFF';
    }

    constructor(sceneObject: SceneObject, data: VideoData) {
        super(sceneObject, data);

        this.videoTexture = null;
        this.quad = null;
        this.aspectRatio = 1;
    }

    /**
     * Validate video data.
     */
    protected validate(data: VideoData): ValidationError[] {
        const errors: ValidationError[] = [];

        // Validate aspect ratio if present
        if (data.aspect !== undefined) {
            if (typeof data.aspect !== 'number' || data.aspect <= 0) {
                errors.push({
                    field: 'aspect',
                    message: 'Aspect ratio must be a positive number',
                    code: 'INVALID_ASPECT'
                });
            }
        }

        this.validateFit(data.fit, errors);
        this.validateCornerRadius(data.cornerRadius, errors);
        this.validatePill(data.pill, errors);
        this.validateBack(data.back, errors);
        this.validateBackColor(data.backColor, errors);

        return errors;
    }

    protected async applyCommit(): Promise<void> {
        this.applyOrientation();

        // Add or remove tap-to-play overlay in response to data changes
        if (this._isTapToPlay && this._videoLoaded) {
            if (!this._playButtonPlane) {
                this._showPlayButton();
                // Show play button if video is currently paused
                const video = this.videoTexture?.video;
                this._updatePlayButtonIcon(!video || video.paused);
            }
        } else if (this._playButtonPlane) {
            this._disposePlayButton();
        }

        // Add or remove timeline controls in response to data changes
        if (this._resolved.controls && this._videoLoaded) {
            if (!this._controlsPlane) {
                this._showControls();
            }
        } else if (this._controlsPlane) {
            this._disposeControls();
        }
    }

    // ── Public runtime control methods ──

    /** Get the underlying HTMLVideoElement, or null if not yet loaded. */
    public getMediaElement(): HTMLVideoElement | null {
        return this.videoTexture?.video ?? null;
    }

    /**
     * Commit the detected aspect ratio as authored data so it persists on save.
     * If metadata has already arrived, commits immediately. Otherwise commits
     * automatically once metadata is available. No-op if aspect is already authored.
     */
    commitAspectWhenAvailable(): void {
        if (this.getAuthored().aspect !== undefined) return;
        if (this._resolved.aspect !== undefined) {
            void this.commit({ aspect: this._resolved.aspect });
            return;
        }
        // Metadata hasn't arrived yet — commit when it does.
        this._authorAspectOnDetect = true;
    }

    /**
     * Play the underlying video element.
     * Respects loop setting from resolved data.
     */
    play(): void {
        const video = this.videoTexture?.video;
        if (!video) return;
        video.loop = this._resolved.loop !== false;
        video.play().catch(() => { /* autoplay blocked by browser policy, OK */ });
        this._updatePlayButtonIcon(false);
    }

    /**
     * Pause the underlying video element.
     */
    pause(): void {
        const video = this.videoTexture?.video;
        if (!video) return;
        video.pause();
        this._updatePlayButtonIcon(true);
    }

    /**
     * Set the volume on the underlying video element.
     * @param volume - Volume level, clamped to 0-1
     */
    setVolume(volume: number): void {
        const video = this.videoTexture?.video;
        if (!video) return;
        video.volume = Math.max(0, Math.min(1, volume));
    }

    /** Whether the video is currently playing (not paused, not ended). */
    isPlaying(): boolean {
        const video = this.videoTexture?.video;
        return !!video && !video.paused && !video.ended;
    }

    /** Current element volume (0-1). Returns 1 when no element is loaded. */
    getVolume(): number {
        return this.videoTexture?.video?.volume ?? 1;
    }

    /**
     * PlayableClock: the element's position while it is producing time, else
     * null (paused, ended, seeking, stalled, not loaded). Video has no start
     * offset, so this is `currentTime` in the owning Timeline's time base.
     */
    getClockTime(): number | null {
        return mediaElementClockTime(this.videoTexture?.video);
    }

    /**
     * Stop playback and reset to the beginning.
     */
    stop(): void {
        const video = this.videoTexture?.video;
        if (!video) return;
        video.pause();
        video.currentTime = 0;
        this._updatePlayButtonIcon(true);
    }

    /**
     * Handle pointer events routed via TraceScene.handlePointerEvent.
     * Toggles playback on select when tap-to-play is enabled.
     */
    public override onPointerEvent(type: 'select' | 'hover' | 'active', isEnter: boolean): void {
        if (type === 'select' && isEnter && this._isTapToPlay) {
            // Don't toggle playback if the controls bar handled this pointer event
            if (this._controlsConsumedPointer) {
                this._controlsConsumedPointer = false;
                return;
            }
            this._togglePlayback();
        }
    }

    /** A tap-to-play video toggles playback on select, so the tap is its own. */
    public override claimsSelect(): boolean {
        return this._isTapToPlay;
    }

    /**
     * Seek the underlying video element.
     * @param time - Target time in seconds, or delta when mode is "relative"
     * @param mode - "absolute" (default) or "relative" — add delta to current time
     */
    seek(time: number, mode: "absolute" | "relative" = "absolute"): void {
        const video = this.videoTexture?.video;
        if (!video) return;
        const target = mode === "relative" ? video.currentTime + time : time;
        // Clamp both ends to match Timeline.seek. Browsers silently ignore
        // out-of-range assignments to currentTime, but clamping here makes
        // the behavior inspectable and consistent. duration is NaN before
        // metadata loads and Infinity for live streams — skip the upper
        // bound in both cases.
        const duration = video.duration;
        const upperClamped = isFinite(duration) ? Math.min(target, duration) : target;
        video.currentTime = Math.max(0, upperClamped);
    }

    /**
     * Capture the currently displayed video frame as a PNG Blob.
     * Returns null if metadata hasn't loaded yet or the frame can't be drawn
     * (e.g. tainted by CORS).
     */
    async captureCurrentFrame(): Promise<Blob | null> {
        const video = this.videoTexture?.video;
        if (!video || !video.videoWidth || !video.videoHeight) return null;
        // readyState < HAVE_CURRENT_DATA means drawImage silently paints
        // transparent black instead of throwing — return null so callers
        // don't get a blank PNG.
        if (video.readyState < 2) return null;
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;
        try {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        } catch {
            return null;
        }
        return new Promise(resolve => canvas.toBlob(b => resolve(b), 'image/png'));
    }

    protected onPlayModeChanged(playing: boolean): void {
        const video = this.videoTexture?.video;
        if (!video) return;

        if (playing) {
            // Play-mode entry is just the first activation event: only an
            // effectively-active object autoplays. One on a hidden page (or
            // under any disabled ancestor) starts when it actually activates —
            // see onEffectiveActiveChanged — instead of playing (audio
            // included) from behind a page the user hasn't navigated to yet
            // (#937).
            if (this._shouldAutoplay()) {
                this.play();
            } else {
                this._updatePlayButtonIcon(true);
            }
        } else {
            // Pause and rewind when leaving play mode
            video.pause();
            video.currentTime = 0;
            this._updatePlayButtonIcon(true);
        }
    }

    /**
     * Activation lifecycle: mirrors AnimationSequence's onEffectiveActiveChanged
     * (see animationsequence.ts) so a video that was hidden at play-mode entry
     * starts the moment it actually activates, instead of never playing (or
     * looping invisibly from t=0). Replaces the old render-loop
     * `_activeObserver` poll — this fires immediately on the flip rather than
     * a frame late, and covers play-mode-entry too via the effectiveActive
     * check above.
     *
     * Deactivation pauses in edit mode too, as the poll did — a video
     * previewed in the editor stops when hidden rather than playing its audio
     * from nowhere. Resuming is play mode only. Activation is re-entry, as
     * for AnimationSequence: a playOnStart video resumes on every activation,
     * from where it paused — even one the user paused before it was hidden.
     */
    protected override onEffectiveActiveChanged(active: boolean): void {
        const video = this.videoTexture?.video;
        if (!video) return;
        if (!active) {
            video.pause();
            this._updatePlayButtonIcon(true);
            return;
        }
        if (this._shouldAutoplay()) {
            this.play();
        }
    }

    /**
     * The one autoplay condition, shared by every entry point that can start
     * playback on its own — play-mode entry, activation, metadata arriving,
     * and the stolen-pause guard — so they can't drift apart: play mode,
     * playOnStart, and an effectively-active object (#937). Read live, since
     * each entry point can fire while any of the three is changing.
     */
    private _shouldAutoplay(): boolean {
        return this.playMode &&
            this._resolved.playOnStart !== false &&
            this.sceneObject.effectiveActive;
    }

    /**
     * Apply the current orientation to the quad mesh.
     */
    private applyOrientation(): void {
        if (!this.quad) return;

        this.orientation = this._resolved.orientation ?? Orientation.Vertical;
        if (this.orientation === Orientation.Horizontal) {
            // Face up (180° around Y, then +90° around X)
            this.quad.rotation = new Vector3(Math.PI / 2, Math.PI, 0);
        } else {
            // Face forward (180° around Y)
            this.quad.rotation = new Vector3(0, Math.PI, 0);
        }
        // Keep play button in sync — default plane faces +Z
        if (this._playButtonPlane) {
            if (this.orientation === Orientation.Horizontal) {
                this._playButtonPlane.rotation = new Vector3(-Math.PI / 2, 0, 0);
                this._playButtonPlane.position.set(0, 0.01, 0);
            } else {
                this._playButtonPlane.rotation = new Vector3(0, 0, 0);
                this._playButtonPlane.position.set(0, 0, 0.01);
            }
        }
        // Keep controls bar in sync
        if (this._controlsPlane) {
            const barHeight = CONTROLS_BAR_HEIGHT;
            if (this.orientation === Orientation.Horizontal) {
                this._controlsPlane.rotation = new Vector3(-Math.PI / 2, 0, 0);
                this._controlsPlane.position.set(0, 0.005, 0.5 - barHeight / 2);
            } else {
                this._controlsPlane.rotation = new Vector3(0, 0, 0);
                this._controlsPlane.position.set(0, -0.5 + barHeight / 2, 0.005);
            }
        }
        this.onOrientationChangedObservable.notifyObservers(this.orientation);
        this.updateQuadFit();
    }

    /** Size/position the overlay meshes against the fitted video rect. */
    protected override onQuadFitUpdated(fw: number, fh: number): void {
        if (this._pickerMesh) {
            this._pickerMesh.scaling.set(fw, fh, 1);
        }
        if (this._controlsPlane) {
            // Controls geometry is built at the fitted width (texture aspect
            // is baked in) — rebuild when the fit changes materially.
            if (Math.abs(this._controlsFittedWidth - fw) > 1e-4) {
                this._disposeControls();
                this._showControls();
            } else {
                const barHeight = CONTROLS_BAR_HEIGHT;
                if (this.orientation === Orientation.Horizontal) {
                    this._controlsPlane.position.set(0, 0.005, fh / 2 - barHeight / 2);
                } else {
                    this._controlsPlane.position.set(0, -fh / 2 + barHeight / 2, 0.005);
                }
            }
        }
    }

    load(): Promise<void> {
        const name = this.sceneObject.name;

        // Use aspect from data, falling back to default (1.33 = 4:3)
        this.aspectRatio = this._resolved.aspect ?? 1.33;
        // Video has always stood behind that 4:3 default, so it counts as
        // known and this load path keeps contain-fitting exactly as before.
        // (Image has no equivalent: nothing authors an aspect for a still, so
        // it fits its box until a texture measures one.)
        this.aspectKnown = true;

        // Create quad immediately, contain-fit to the authored box
        const videoQuad = MeshBuilder.CreatePlane(name, { size: 1 }, this.scene);
        videoQuad.name = `${name}_mesh`;

        // Flip UVs to compensate for the 180° Y rotation and texture coordinate
        // convention. Done on geometry so material overrides display correctly.
        const uvs = videoQuad.getVerticesData("uv");
        if (uvs) {
            for (let i = 0; i < uvs.length; i += 2) {
                uvs[i] = 1 - uvs[i];
                uvs[i + 1] = 1 - uvs[i + 1];
            }
            videoQuad.setVerticesData("uv", uvs);
        }
        this.quad = videoQuad;
        this.applyOrientation();
        this.sceneObject.addChild(videoQuad);
        this.watchNodeScaleForFit();

        // Create material - starts black until video loads
        const videoMaterial = new StandardMaterial(`${name}_video_mat`, this.scene);
        videoMaterial.diffuseColor.set(0, 0, 0);
        videoMaterial.emissiveColor.set(0, 0, 0);
        videoQuad.material = videoMaterial;
        this.videoMaterial = videoMaterial;

        // Pause/resume on enabled-state changes (including ancestors) is
        // handled by onEffectiveActiveChanged — it fires for the whole
        // subtree on an ancestor flip (SceneObject._refreshEffectiveActive),
        // which a per-node onBeforeRenderObservable poll can't see directly.

        // Check if already enabled (including ancestors)
        if (this.sceneObject.transform.isEnabled()) {
            // Await metadata so aspectRatio is resolved before load() completes
            return this._loadVideo();
        } else {
            // Defer video loading until node becomes visible
            // Use render observer since onEnabledStateChangedObservable doesn't fire for parent changes
            this._renderObserver = this.scene.onBeforeRenderObservable.add(() => {
                if (!this._videoLoaded && this.sceneObject.transform.isEnabled()) {
                    void this._loadVideo();
                    // Remove observer once loaded
                    if (this._renderObserver) {
                        this.scene.onBeforeRenderObservable.remove(this._renderObserver);
                        this._renderObserver = null;
                    }
                }
            });
            return Promise.resolve();
        }
    }

    /**
     * Actually load the video texture (called immediately or deferred until enabled).
     *
     * This method resolves immediately — it does NOT wait for video metadata.
     * The initial layout uses the aspect ratio from scene data; when metadata
     * eventually arrives (or the user presses play on iOS), the `onVideoReady`
     * callback fills in the aspect ratio (when none is authored) and swaps in
     * the real texture.
     *
     * Why: Babylon's VideoTexture with `autoPlay:false` tries a muted
     * play-then-pause to capture a frame.  On iOS Safari, the browser
     * blocks even muted playback without a user gesture, so the "canplay"
     * event never fires and onLoadObservable never triggers — hanging the
     * load promise and blocking the entire scene.
     *
     * @param explicitUrl When provided, use this URL instead of resolving from
     *                    `sceneObject.src`. Used by `setSource()` to swap the
     *                    underlying source at runtime without touching authored data.
     */
    private _loadVideo(explicitUrl?: string): Promise<void> {
        if (this._videoLoaded || !this.quad || !this.videoMaterial) {
            return Promise.resolve();
        }
        this._videoLoaded = true;

        Logger.debug(`loading video ${this.sceneObject.name}`);

        // Show loading spinner until metadata arrives
        this._showLoadingSpinner();

        // Resolve video URL — explicit override wins, otherwise try cached blob
        // (covers both local paths and HTTP URLs with a registered URL mapping),
        // fall back to direct URL.
        let videoUrl = explicitUrl ?? (this.sceneObject.src || "");
        const videoBlob = this.resources.getAsset(videoUrl);
        if (videoBlob) {
            videoUrl = URL.createObjectURL(videoBlob);
        } else {
            // Nothing cached: a folder document's relative src resolves
            // against the document's own URL (spec §2.3).
            videoUrl = this.resources.resolveAssetUrl(videoUrl) ?? videoUrl;
        }

        if (!videoUrl) {
            Logger.warn(`[Video] No video source specified for "${this.sceneObject.name}"`);
            this._hideLoadingSpinner();
            return Promise.resolve();
        }

        this._resolvedVideoUrl = videoUrl;

        // Check cache for existing texture with same URL
        const cached = videoTextureCache.get(videoUrl);
        let videoTexture: VideoTexture;

        if (cached) {
            // Reuse existing texture
            videoTexture = cached.texture;
            cached.refCount++;
            Logger.debug(`[Video] Reusing cached video texture for ${this.sceneObject.name} (refCount: ${cached.refCount})`);
        } else {
            // Create new texture
            // Note: generateMipMaps=false to avoid GL_INVALID_OPERATION warnings
            // (video textures don't support mipmap generation)
            videoTexture = new VideoTexture(
                this.sceneObject.name,
                videoUrl,
                this.scene,
                false, // generateMipMaps - videos don't support this
                true,  // invertY
                undefined, // samplingMode
                { autoPlay: false }
            );

            // Cache it
            videoTextureCache.set(videoUrl, { texture: videoTexture, refCount: 1 });
            Logger.debug(`[Video] Created new video texture for ${this.sceneObject.name}`);
        }

        const video = videoTexture.video;
        this.videoTexture = videoTexture;

        // Allow the browser to fetch metadata (duration, dimensions) eagerly.
        // Babylon may have set this to something else; we force "metadata" so
        // iOS Safari will fetch headers without attempting full buffering.
        // This means when the user taps play the video can start near-instantly.
        video.preload = "metadata";

        const videoMaterial = this.videoMaterial;

        let ready = false;
        const onVideoReady = () => {
            if (ready) return;
            ready = true;

            this._hideLoadingSpinner();

            if (video.videoWidth && video.videoHeight && this._resolved.aspect === undefined) {
                // No authored aspect — use detected dimensions as a runtime fallback.
                // Only _resolved is written so editors that diff authored data
                // (e.g. TraceFrontend) don't see a spurious dirty state.
                this.aspectRatio = video.videoWidth / video.videoHeight;
                this._resolved.aspect = this.aspectRatio;
                this.updateQuadFit();
                if (this._authorAspectOnDetect) {
                    this._authorAspectOnDetect = false;
                    void this.commit({ aspect: this.aspectRatio });
                }
                this.onAspectChangedObservable.notifyObservers(this.aspectRatio);
            }

            // Apply the video texture and reset colors to white.
            videoMaterial.diffuseTexture = videoTexture;
            videoMaterial.emissiveTexture = videoTexture;
            videoMaterial.diffuseColor.set(1, 1, 1);
            videoMaterial.emissiveColor.set(1, 1, 1);

            // Set up tap-to-play overlay if enabled
            if (this._isTapToPlay) {
                this._showPlayButton();
            }

            // Set up timeline controls if enabled
            if (this._resolved.controls) {
                this._showControls();
            }

            // Notify subscribers (e.g. AudioSource) that the media element is ready.
            this.onMediaElementReadyObservable.notifyObservers(video);

            // Determine whether video should be playing right now. Metadata
            // can arrive after the object was hidden (e.g. a Pages deck
            // deactivated its page while the fetch was in flight), which
            // _shouldAutoplay's live effectiveActive read covers (#937).
            if (this._shouldAutoplay()) {
                video.loop = this._resolved.loop !== false;
                video.play().catch(() => { /* autoplay blocked by browser policy, OK */ });
                this._updatePlayButtonIcon(false);

                // Babylon's VideoTexture with autoPlay:false runs a muted
                // play-then-pause trick on "canplay" to capture a frame.
                // If that fires *after* the user has already started
                // playback, Babylon pauses the video behind our back.
                // Only guard when Babylon's trick hasn't completed yet
                // (readyState < 2); otherwise the pause listener would
                // fire on a legitimate user pause or natural video end.
                if (video.readyState < 2) {
                    const onStolenPause = () => {
                        // Re-checks effectiveActive too (#937): a legitimate
                        // visibility-driven pause (onEffectiveActiveChanged)
                        // fires this same 'pause' event, and must not be
                        // resumed just because play mode + playOnStart hold.
                        if (this._shouldAutoplay()) {
                            video.play().catch(() => { /* blocked */ });
                        }
                    };
                    video.addEventListener('pause', onStolenPause, { once: true });
                }
            } else if (video.paused) {
                // Not playing — seek to a tiny offset to force the browser
                // to decode a frame so the texture isn't black.
                video.currentTime = 0.001;
                this._updatePlayButtonIcon(true);
            }
        };

        // Listen for errors so a broken URL doesn't leave the spinner stuck.
        // Babylon builds <source> children for each URL in the array; browsers
        // fire `error` on those elements, not on <video> itself.  Listen on
        // both to cover single-URL and multi-URL fallback cases.
        const onVideoError = () => {
            Logger.warn(`[Video] Failed to load video for "${this.sceneObject.name}": ${video.error?.message || 'unknown error'}`);
            this._hideLoadingSpinner();
        };
        video.addEventListener('error', onVideoError, { once: true });
        for (const source of video.querySelectorAll('source')) {
            source.addEventListener('error', onVideoError, { once: true });
        }

        if (video.readyState >= 2) {
            // Already have metadata (cached texture)
            onVideoReady();
        } else {
            // Babylon's onLoadObservable fires after its internal muted
            // play-then-pause succeeds (desktop).  On iOS this never fires
            // because the muted play is blocked, so we also listen for
            // "loadedmetadata" directly to pick up aspect ratio and apply
            // the texture as soon as the browser has dimensions.
            // Whichever fires first wins (onVideoReady is idempotent).
            videoTexture.onLoadObservable.addOnce(() => {
                onVideoReady();
            });
            video.addEventListener('loadedmetadata', () => {
                onVideoReady();
            }, { once: true });
        }

        // Resolve immediately — scene loading is not gated on video metadata.
        return Promise.resolve();
    }

    /**
     * Show a loading spinner overlay on the video quad
     */
    private _showLoadingSpinner(): void {
        if (!this.quad || !this.scene) return;

        // Create a separate plane for the spinner overlay, slightly in front of the video quad
        const spinnerPlane = MeshBuilder.CreatePlane("spinnerPlane", { size: 0.3 }, this.scene);
        spinnerPlane.parent = this.quad;
        spinnerPlane.position.z = -0.01; // Slightly in front
        this._loadingPlane = spinnerPlane;

        // Create GUI texture on the spinner plane (not the video quad)
        const texture = AdvancedDynamicTexture.CreateForMesh(spinnerPlane, 128, 128);
        this._loadingTexture = texture;

        // Create spinning arc indicator
        const spinner = new Ellipse("spinner");
        spinner.width = "100px";
        spinner.height = "100px";
        spinner.color = "white";
        spinner.thickness = 6;
        spinner.background = "transparent";
        spinner.arc = 0.75;
        texture.addControl(spinner);

        // Animate rotation
        let rotation = 0;
        this._spinnerObserver = this.scene.onBeforeRenderObservable.add(() => {
            rotation += 0.02;
            spinner.rotation = rotation;
        });
    }

    /**
     * Hide and dispose the loading spinner
     */
    private _hideLoadingSpinner(): void {
        if (this._spinnerObserver && this.scene) {
            this.scene.onBeforeRenderObservable.remove(this._spinnerObserver);
            this._spinnerObserver = null;
        }

        if (this._loadingTexture) {
            this._loadingTexture.dispose();
            this._loadingTexture = null;
        }

        if (this._loadingPlane) {
            this._loadingPlane.dispose();
            this._loadingPlane = null;
        }
    }

    /** Whether tap-to-play is enabled via userInteractible or controls */
    private get _isTapToPlay(): boolean {
        return this._resolved.userInteractible === true || this._resolved.controls === true;
    }

    /**
     * Show the play/pause button overlay on the video.
     * Click detection is handled by onPointerEvent (routed from TraceScene
     * via ClickToSelect). If no BoxCollider exists, we create an invisible
     * picker mesh so there's something for ray picking to hit.
     */
    private _showPlayButton(): void {
        if (!this.quad || !this.scene || this._playButtonPlane) return;

        // Play button plane — sibling of quad (parented to SceneObject
        // transform) so it isn't affected by the quad's aspect scaling.
        // Default plane faces +Z — same direction the camera looks from,
        // so no rotation needed for vertical orientation.
        const buttonPlane = MeshBuilder.CreatePlane(
            `${this.sceneObject.name}_playBtn`,
            { size: 0.3, sideOrientation: Mesh.BACKSIDE },
            this.scene
        );
        this.sceneObject.addChild(buttonPlane);
        if (this.orientation === Orientation.Horizontal) {
            buttonPlane.rotation = new Vector3(-Math.PI / 2, 0, 0);
            buttonPlane.position.set(0, 0.01, 0);
        } else {
            buttonPlane.position.set(0, 0, 0.01);
        }
        buttonPlane.isPickable = false;
        this._playButtonPlane = buttonPlane;

        const texture = AdvancedDynamicTexture.CreateForMesh(buttonPlane, 128, 128, false);
        texture.hasAlpha = true;
        if (buttonPlane.material) {
            // CreateForMesh sets backFaceCulling=false; restore to true
            // so the play button is only visible from the front (+Z)
            buttonPlane.material.backFaceCulling = true;
        }
        this._playButtonTexture = texture;

        const circle = new Ellipse("play-circle");
        circle.width = "100px";
        circle.height = "100px";
        circle.thickness = 3;
        circle.color = "white";
        circle.background = "rgba(0, 0, 0, 0.5)";
        texture.addControl(circle);

        const icon = new TextBlock("play-icon", "\u25B6");
        icon.color = "white";
        icon.fontSize = 48;
        icon.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
        icon.textVerticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
        icon.paddingLeft = "6px";
        circle.addControl(icon);

        // Create invisible picker mesh if no BoxCollider exists on this object
        this._ensurePickerMesh();
    }

    /** Toggle video play/pause and update the overlay icon */
    private _togglePlayback(): void {
        const video = this.videoTexture?.video;
        if (!video) return;

        if (video.paused) {
            video.loop = this._resolved.loop !== false;
            video.play().catch(() => { /* autoplay blocked */ });
            this._updatePlayButtonIcon(false);
        } else {
            video.pause();
            this._updatePlayButtonIcon(true);
        }
    }

    /** Update play button icon: show play (▶) when paused, hide when playing */
    private _updatePlayButtonIcon(paused: boolean): void {
        if (!this._playButtonPlane) return;
        // Show overlay when paused, hide when playing
        this._playButtonPlane.setEnabled(paused);
    }

    // ── Timeline controls ──

    /** Show the timeline controls bar at the bottom of the video */
    private _showControls(): void {
        if (!this.quad || !this.scene || this._controlsPlane) return;

        // Bar spans the FITTED video width (quad local scale = fitted rect in
        // node units), thin strip at the video's bottom edge.
        const fw = this.quad.scaling.x;
        const fh = this.quad.scaling.y;
        const barHeight = CONTROLS_BAR_HEIGHT;
        const controlsPlane = MeshBuilder.CreatePlane(
            `${this.sceneObject.name}${CONTROLS_MESH_SUFFIX}`,
            { width: fw, height: barHeight, sideOrientation: Mesh.DOUBLESIDE },
            this.scene
        );
        this._controlsFittedWidth = fw;
        // Sibling of quad (parented to SceneObject transform) so it isn't
        // affected by the quad's 180° Y rotation. Same pattern as play button.
        this.sceneObject.addChild(controlsPlane);
        if (this.orientation === Orientation.Horizontal) {
            controlsPlane.rotation = new Vector3(-Math.PI / 2, 0, 0);
            controlsPlane.position.set(0, 0.005, fh / 2 - barHeight / 2);
        } else {
            controlsPlane.position.set(0, -fh / 2 + barHeight / 2, 0.005);
        }
        // Must be pickable so scene pointer picks hit it for scrubbing
        controlsPlane.isPickable = true;
        // Prevent XR hand-grab from claiming this mesh. SixDofDragBehavior
        // flips `isNearGrabbable = true` on all descendants of the grabbed
        // transform on attach; XRManager re-clears the flag for our suffix
        // after each attach, so in XR the XRManager re-clear is what
        // actually sticks. This pre-set covers the non-XR selected-object
        // path (e.g. unit tests) and defense-in-depth.
        controlsPlane.isNearGrabbable = false;
        this._controlsPlane = controlsPlane;

        // Texture — visual only, no GUI pointer interaction (we handle scrub
        // via scene pointer observable, same pattern as DragToMove).
        // Resolution must match plane aspect ratio to avoid squishing.
        const texW = 1024;
        // Clamp: fw is the FITTED width and shrinks toward 0 for extreme
        // video-vs-box aspect mismatches (a portrait video in a wide short
        // slot) — unclamped, that requests an absurdly tall texture.
        const texH = Math.min(512, Math.round(texW * (barHeight / Math.max(fw, 0.05))));
        const texture = AdvancedDynamicTexture.CreateForMesh(controlsPlane, texW, texH, false);
        texture.hasAlpha = true;
        if (controlsPlane.material) {
            controlsPlane.material.backFaceCulling = true;
        }
        this._controlsTexture = texture;

        // Semi-transparent background
        const bg = new Rectangle("controls-bg");
        bg.width = 1;
        bg.height = 1;
        bg.thickness = 0;
        bg.background = "rgba(0, 0, 0, 0.55)";
        texture.addControl(bg);

        // Progress bar track (upper portion of bar)
        const track = new Rectangle("track");
        track.width = CONTROLS_TRACK_WIDTH;
        track.height = 0.15;
        track.thickness = 0;
        track.background = "rgba(255, 255, 255, 0.25)";
        track.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
        track.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        track.top = "15%";
        bg.addControl(track);

        // Progress bar fill
        const fill = new Rectangle("fill");
        fill.width = 0;
        fill.height = 1;
        fill.thickness = 0;
        fill.background = "white";
        fill.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        track.addControl(fill);
        this._progressFill = fill;

        // Time label (below track, right-aligned)
        const timeText = new TextBlock("time", "0:00 / 0:00");
        timeText.color = "white";
        timeText.fontSize = 0.35 * texH;
        timeText.height = "60%";
        timeText.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
        timeText.textVerticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
        timeText.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
        timeText.paddingBottom = "25%";
        timeText.paddingRight = "8%";
        bg.addControl(timeText);
        this._timeText = timeText;

        // Frame update: sync progress bar and time text with video
        this._controlsUpdateObserver = this.scene.onBeforeRenderObservable.add(() => {
            const video = this.videoTexture?.video;
            if (!video || !this._progressFill) return;
            const progress = video.duration > 0 ? video.currentTime / video.duration : 0;
            this._progressFill.width = progress;
            if (this._timeText) {
                this._timeText.text = `${formatTime(video.currentTime)} / ${formatTime(video.duration)}`;
            }
        });

        // Scrub interaction via scene pointer observable.
        // Uses onPrePointerObservable so we can block camera orbit during drag
        // (same detach/reattach pattern as DragToMove).
        const pickControls = (pi: PointerInfoPre) => {
            // In XR, `pi.ray` is the controller's world-space pointer ray.
            // `pi.localPosition` is a synthesized 2D coord that doesn't map
            // meaningfully to the XR camera's viewport, so 2D picking misses.
            if (pi.ray) {
                return this.scene.pickWithRay(pi.ray, (m) => m === controlsPlane);
            }
            return this.scene.pick(
                pi.localPosition.x, pi.localPosition.y,
                (m) => m === controlsPlane,
            );
        };

        this._controlsPointerObserver = this.scene.onPrePointerObservable.add((pi) => {
            switch (pi.type) {
                case PointerEventTypes.POINTERDOWN: {
                    if (this._isScrubbing) return;
                    // Pick to see if this pointer-down hits the controls mesh
                    const pick = pickControls(pi);
                    if (!pick?.hit || !pick.getTextureCoordinates()) return;

                    this._isScrubbing = true;
                    this._controlsConsumedPointer = true;
                    // Block camera orbit and downstream pointer handlers
                    pi.skipOnPointerObservable = true;
                    this.scene.activeCamera?.detachControl();
                    this._scrubFromUV(pick.getTextureCoordinates()!.x);
                    break;
                }
                case PointerEventTypes.POINTERMOVE: {
                    if (!this._isScrubbing) return;
                    pi.skipOnPointerObservable = true;
                    // Pick against controls mesh for updated UV
                    const pick = pickControls(pi);
                    if (pick?.hit) {
                        const uv = pick.getTextureCoordinates();
                        if (uv) this._scrubFromUV(uv.x);
                    }
                    break;
                }
                case PointerEventTypes.POINTERUP: {
                    if (!this._isScrubbing) return;
                    this._isScrubbing = false;
                    // Since skipOnPointerObservable blocked the down/up from
                    // reaching ClickToSelect, onPointerEvent was never called —
                    // clear the flag so the next real click isn't swallowed.
                    this._controlsConsumedPointer = false;
                    pi.skipOnPointerObservable = true;
                    // Reattach camera (same pattern as DragToMove)
                    const canvas = this.scene.getEngine().getRenderingCanvas();
                    if (this.scene.activeCamera && canvas) {
                        this.scene.activeCamera.attachControl(canvas, true);
                    }
                    break;
                }
            }
        });
    }

    /** Scrub the video using the U coordinate (0–1 across the controls plane width) */
    private _scrubFromUV(u: number): void {
        const video = this.videoTexture?.video;
        if (!video || !video.duration) return;
        // Track is centered at CONTROLS_TRACK_WIDTH of the bar
        const trackLeft = (1 - CONTROLS_TRACK_WIDTH) / 2;
        const trackRight = 1 - trackLeft;
        const ratio = Math.max(0, Math.min(1, (u - trackLeft) / (trackRight - trackLeft)));
        video.currentTime = ratio * video.duration;
    }

    /** Dispose the timeline controls overlay */
    private _disposeControls(): void {
        // Reattach camera if disposed mid-scrub (e.g. controls toggled off while dragging)
        if (this._isScrubbing) {
            const canvas = this.scene?.getEngine().getRenderingCanvas();
            if (this.scene?.activeCamera && canvas) {
                this.scene.activeCamera.attachControl(canvas, true);
            }
        }
        if (this._controlsUpdateObserver && this.scene) {
            this.scene.onBeforeRenderObservable.remove(this._controlsUpdateObserver);
            this._controlsUpdateObserver = null;
        }
        if (this._controlsPointerObserver && this.scene) {
            this.scene.onPrePointerObservable.remove(this._controlsPointerObserver);
            this._controlsPointerObserver = null;
        }
        if (this._controlsTexture) {
            this._controlsTexture.dispose();
            this._controlsTexture = null;
        }
        if (this._controlsPlane) {
            this._controlsPlane.dispose();
            this._controlsPlane = null;
        }
        this._progressFill = null;
        this._timeText = null;
        this._isScrubbing = false;
        this._controlsConsumedPointer = false;
    }

    /** Dispose tap-to-play overlay and picker mesh */
    private _disposePlayButton(): void {
        if (this._playButtonTexture) {
            this._playButtonTexture.dispose();
            this._playButtonTexture = null;
        }
        if (this._playButtonPlane) {
            this._playButtonPlane.dispose();
            this._playButtonPlane = null;
        }
        this._disposePickerMesh();
    }

    /**
     * Create an invisible picker mesh so ClickToSelect can pick this object.
     * Only created when no BoxCollider exists — if the user has a BoxCollider,
     * that handles picking already.
     *
     * TODO: If a BoxCollider is added after the picker mesh was created, both
     * meshes coexist with COLLIDER_MESH_SUFFIX names. Consider disposing the
     * picker mesh when a BoxCollider is attached to this SceneObject.
     */
    private _ensurePickerMesh(): void {
        if (this._pickerMesh) return;
        if (this.sceneObject.getBehavior(BoxCollider)) return;

        // Unit box scaled to the fitted video rect, so _layoutOverlays can
        // track fit changes by scaling instead of rebuilding geometry.
        this._pickerMesh = MeshBuilder.CreateBox(
            `${this.sceneObject.name}${COLLIDER_MESH_SUFFIX}`,
            { width: 1, height: 1, depth: 0.01 },
            this.scene
        );
        this._pickerMesh.scaling.set(
            this.quad?.scaling.x ?? this.aspectRatio,
            this.quad?.scaling.y ?? 1,
            1,
        );
        this._pickerMesh.parent = this.sceneObject.transform;
        this._pickerMesh.visibility = 0;
        this._pickerMesh.isPickable = true;
    }

    /** Dispose the auto-created picker mesh */
    private _disposePickerMesh(): void {
        if (this._pickerMesh) {
            this._pickerMesh.dispose();
            this._pickerMesh = null;
        }
    }

    /**
     * Release the current video texture: pauses playback, decrements its
     * refcount in the shared cache, and disposes the texture if this was the
     * last reference. Leaves the quad/material intact so they can be reused
     * (e.g. when swapping source via setSource).
     */
    private _releaseVideoTexture(): void {
        const video = this.videoTexture?.video;
        if (video) {
            video.pause();
        }
        if (this.videoTexture && this._resolvedVideoUrl) {
            const cached = videoTextureCache.get(this._resolvedVideoUrl);
            if (cached) {
                cached.refCount--;
                if (cached.refCount <= 0) {
                    cached.texture.dispose();
                    videoTextureCache.delete(this._resolvedVideoUrl);
                    Logger.debug(`[Video] Disposed cached video texture for ${this._resolvedVideoUrl}`);
                } else {
                    Logger.debug(`[Video] Decremented refCount for ${this._resolvedVideoUrl} (refCount: ${cached.refCount})`);
                }
            } else {
                this.videoTexture.dispose();
            }
        }
        this.videoTexture = null;
        this._resolvedVideoUrl = null;
        this._videoLoaded = false;
    }

    /**
     * Swap the underlying video source URL at runtime. Pauses playback,
     * releases the current texture, then loads the new URL. Authored
     * `sceneObject.src` is left unchanged (this is a runtime-only override).
     *
     * Idempotent: returns immediately if the URL matches the current source.
     *
     * The returned Promise resolves when the underlying HTMLVideoElement has
     * metadata available (the same point `onMediaElementReadyObservable`
     * fires). Callers that need to call `play()`/`seek()`/`setVolume()` on
     * the new source should await this promise — calling those methods
     * synchronously after `setSource` would otherwise hit a null videoTexture.
     */
    public setSource(url: string): Promise<void> {
        if (!url) {
            Logger.warn(`[Video] setSource called with empty URL on "${this.sceneObject.name}"`);
            return Promise.resolve();
        }
        // If the new URL resolves to a blob, the cache lookup below already
        // dedupes; if it's a direct URL, we can short-circuit when unchanged.
        const blob = this.resources.getAsset(url);
        const wouldResolveTo = blob ? null : url;
        if (wouldResolveTo && this._resolvedVideoUrl === wouldResolveTo) {
            return Promise.resolve();
        }

        // Reset detected-aspect detection so the new source's aspect can apply
        // (only when no authored aspect — same gating as initial load).
        this._releaseVideoTexture();
        // Reset visible material to black until the new texture is ready.
        if (this.videoMaterial) {
            this.videoMaterial.diffuseTexture = null;
            this.videoMaterial.emissiveTexture = null;
            this.videoMaterial.diffuseColor.set(0, 0, 0);
            this.videoMaterial.emissiveColor.set(0, 0, 0);
        }
        // `_loadVideo` returns immediately (it doesn't wait for metadata —
        // intentional for scene startup so a missing video doesn't block load).
        // For setSource we want the opposite: callers need a real signal of
        // "the new element is ready," so we hook the media-ready observable.
        const ready = new Promise<void>((resolve) => {
            this.onMediaElementReadyObservable.addOnce(() => resolve());
        });
        void this._loadVideo(url);
        return ready;
    }

    protected onDetach(): void {
        this.disposeFitWatcher();
        this.disposeBackCard();

        // Clean up render observer
        if (this._renderObserver && this.scene) {
            this.scene.onBeforeRenderObservable.remove(this._renderObserver);
            this._renderObserver = null;
        }

        // Clean up loading spinner, play button (which disposes picker mesh too), and controls
        this._hideLoadingSpinner();
        this._disposePlayButton();
        this._disposeControls();

        // Release the video texture (handles cache refcount).
        this._releaseVideoTexture();

        if (this.quad) {
            this.quad = null;
        }

        this.videoMaterial = null;
        this.onMediaElementReadyObservable.clear();

        Logger.debug(`Video ${this.sceneObject.name} detached and cleaned up.`);
    }
}
